import { useEffect, useState } from 'react';
import { X, AlertCircle, CheckCircle2, Loader2, FileUp } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminApi';

export default function ImportArtistsModal({ file, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [conflictAction, setConflictAction] = useState('skip'); // 'skip' | 'overwrite'

  useEffect(() => {
    if (!file) return;
    let alive = true;
    setBusy(true);
    adminApi.importPreview(file)
      .then(d => { if (alive) setPreview(d); })
      .catch(e => { toast.error(e?.response?.data?.detail || 'Could not parse CSV'); if (alive) onClose(); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [file, onClose]);

  const commit = async () => {
    if (!preview?.rows?.length) return;
    setCommitting(true);
    try {
      const res = await adminApi.importCommit(preview.rows, conflictAction);
      toast.success(`Import done — ${res.created} created · ${res.updated} updated · ${res.skipped} skipped`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setCommitting(false);
    }
  };

  const hasConflicts = (preview?.conflicts?.length || 0) > 0;
  const hasErrors = (preview?.errors?.length || 0) > 0;

  return (
    <div data-testid="import-modal" className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[#eaeaea]">
          <div className="flex items-center gap-3">
            <FileUp className="w-5 h-5 text-[#eb5381]" />
            <h2 className="font-serif text-2xl">Import Artists from CSV</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center hover:bg-[#fafafa]" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5">
          {busy && (
            <div className="flex items-center gap-3 text-sm text-neutral-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Parsing {file?.name}…
            </div>
          )}

          {!busy && preview && (
            <>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="border border-[#eaeaea] p-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Total Rows</div>
                  <div className="font-serif text-3xl mt-1">{preview.total}</div>
                </div>
                <div className="border border-[#eaeaea] p-4 bg-[#f0fdf4]">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">New Artists</div>
                  <div className="font-serif text-3xl mt-1 text-[#16a34a]">{preview.new.length}</div>
                </div>
                <div className="border border-[#eaeaea] p-4 bg-[#fff8ee]">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Slug Conflicts</div>
                  <div className="font-serif text-3xl mt-1 text-[#eb5381]">{preview.conflicts.length}</div>
                </div>
              </div>

              {hasErrors && (
                <div className="border border-red-300 bg-red-50 p-4">
                  <div className="flex items-center gap-2 text-red-700 text-[11px] uppercase tracking-[0.2em] mb-2">
                    <AlertCircle className="w-4 h-4" /> {preview.errors.length} rows skipped (invalid)
                  </div>
                  <ul className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                    {preview.errors.map((e, i) => <li key={i}>Row {e.row}: {e.reason}{e.slug ? ` (slug: ${e.slug})` : ''}</li>)}
                  </ul>
                </div>
              )}

              {hasConflicts && (
                <div className="border border-[#eaeaea] p-5">
                  <div className="font-serif text-xl mb-2">Slug conflicts found — please choose</div>
                  <p className="text-xs text-neutral-600 mb-4">
                    {preview.conflicts.length} artist{preview.conflicts.length > 1 ? 's' : ''} in your CSV already exist in the database with the same slug. Decide how to handle them:
                  </p>
                  <div className="space-y-2.5">
                    <label className="flex items-start gap-3 cursor-pointer text-sm" data-testid="conflict-action-skip">
                      <input type="radio" name="conflict_action" value="skip" checked={conflictAction === 'skip'} onChange={() => setConflictAction('skip')} className="mt-1 accent-[#eb5381]" />
                      <div>
                        <div className="font-medium">Skip conflicting rows</div>
                        <div className="text-xs text-neutral-500">Keep existing artists unchanged. Only new ones will be added.</div>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer text-sm" data-testid="conflict-action-overwrite">
                      <input type="radio" name="conflict_action" value="overwrite" checked={conflictAction === 'overwrite'} onChange={() => setConflictAction('overwrite')} className="mt-1 accent-[#eb5381]" />
                      <div>
                        <div className="font-medium text-[#eb5381]">Overwrite existing artists</div>
                        <div className="text-xs text-neutral-500">Replace each conflicting artist with the data from your CSV. This cannot be undone.</div>
                      </div>
                    </label>
                  </div>
                  <details className="mt-4">
                    <summary className="text-[10px] uppercase tracking-[0.25em] cursor-pointer text-neutral-500 hover:text-[#eb5381]">Show conflict list ({preview.conflicts.length})</summary>
                    <ul className="mt-2 text-xs space-y-1 max-h-32 overflow-y-auto bg-[#fafafa] p-3">
                      {preview.conflicts.map((c, i) => <li key={i}>Row {c.row}: <span className="font-medium">{c.name}</span> ({c.slug})</li>)}
                    </ul>
                  </details>
                </div>
              )}

              {!hasConflicts && preview.new.length > 0 && (
                <div className="border border-[#16a34a]/30 bg-[#f0fdf4] p-4 flex items-center gap-3 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#16a34a] shrink-0" />
                  <span>All {preview.new.length} rows look good and will be added as new artists.</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-t border-[#eaeaea] p-5 flex items-center justify-between gap-3">
          <button onClick={onClose} className="text-[11px] uppercase tracking-[0.25em] text-neutral-600 hover:text-black">
            Cancel
          </button>
          <button
            onClick={commit}
            disabled={busy || committing || !preview?.rows?.length}
            data-testid="confirm-import-btn"
            className="inline-flex items-center gap-2 bg-[#eb5381] hover:bg-[#d64570] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 text-[11px] uppercase tracking-[0.25em] transition-colors"
          >
            {committing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
            {committing
              ? 'Importing…'
              : hasConflicts
                ? (conflictAction === 'overwrite' ? `Overwrite & Import ${preview?.rows?.length || 0}` : `Skip Conflicts & Import ${(preview?.new?.length || 0)}`)
                : `Confirm Import (${preview?.rows?.length || 0})`}
          </button>
        </div>
      </div>
    </div>
  );
}
